/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { forwardRef, useMemo } from 'react';
import * as React from 'react';

import config from 'js/app/config';
import Imgix from 'js/components/Imgix';
import path from 'js/lib/path';
import type { TrackingData } from 'js/lib/retracked';

import { Hidden, ProductCard, Typography2, VisuallyHidden, breakpoints, typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import CompactCareerRoleCardPlaceholder from 'bundles/front-page/redesign-modules/career-academy/CompactCareerRoleCardPlaceholder';
import JobsAvailableInfo from 'bundles/front-page/redesign-modules/career-academy/JobsAvailableInfo';
import SalaryInfo from 'bundles/front-page/redesign-modules/career-academy/SalaryInfo';
import type { CareerRoleType } from 'bundles/unified-common/types/careerRoles';

import _t from 'i18n!nls/front-page';

const IMAGE_DIMENSIONS = {
  MAX_WIDTH: 305,
  MAX_HEIGHT: 125,
} as const;

const styles = {
  wrapper: css`
    display: grid;
    width: 100%;
    max-width: 100%;
  `,
  container: css`
    cursor: pointer;
  `,
  bodyContainer: css`
    display: flex;
    flex-direction: column;
    height: 100%;

    h3,
    strong {
      color: var(--cds-color-neutral-primary);
      font-weight: 600;
    }
  `,
  description: css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
    margin-bottom: var(--cds-spacing-100);
  `,
  roleStatsContainer: css`
    margin-top: auto;

    ${breakpoints.down('xs')} {
      p {
        ${typography2.bodyTertiary}
      }
    }
  `,
  productCard: css`
    border-radius: var(--cds-border-radius-100) !important;

    .cds-ProductCard-content {
      gap: var(--cds-spacing-100) !important;
      padding-right: var(--cds-spacing-300) !important;
    }

    .cds-CommonCard-previewImage {
      margin-bottom: 0 !important;
      aspect-ratio: auto !important;

      img {
        object-fit: contain;
      }
    }

    ${breakpoints.down('md')} {
      max-width: 321px !important;
      min-width: unset !important;
    }

    ${breakpoints.down('xs')} {
      .cds-ProductCard-gridCard {
        padding: 0 !important;
      }

      .cds-CommonCard-previewImage {
        border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0 !important;
      }

      .cds-CommonCard-title {
        ${typography2.subtitleSmall};
        margin-bottom: var(--cds-spacing-100);
      }

      .cds-ProductCard-content {
        padding-right: var(--cds-spacing-100) !important;
      }
    }
  `,
  imagePrimaryColor: (imagePrimaryColor?: string) => css`
    .cds-CommonCard-previewImage img {
      background-color: ${imagePrimaryColor ?? 'transparent'};
    }
  `,
};

type Props = Partial<Pick<CareerRoleType, 'merchandisingSkillNames' | 'skills'>> &
  Omit<CareerRoleType, 'merchandisingSkillNames' | 'skills'> & {
    showLoadingState?: boolean;
    eventingData?: TrackingData;
    imagePrimaryColor?: string;
    description?: string;
  };

/**
 * @description Displays a career role in a compact, responsive card format.
 * Offers desktop and mobile (<600px) variants, with loading states for both.
 */
const CompactCareerRoleCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      name: roleName,
      description,
      roleCardImageUrl,
      imagePrimaryColor,
      jobPostingData,
      slug,
      eventingData,
      showLoadingState,
    },
    ref
  ) => {
    const track = useTracker();

    const careerCardEventingV3Data = useMemo(
      () => ({
        ...eventingData,
        role: {
          roleSlug: slug || '',
        },
      }),
      [eventingData, slug]
    );

    const visibilityRef = useVisibilityTracker('view_role_card', careerCardEventingV3Data);

    const combinedRef = useMemo(
      () => (node: HTMLDivElement | null) => {
        (visibilityRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        }
      },
      [ref, visibilityRef]
    );

    const handleSelectCard = () => {
      track('click_role_card', careerCardEventingV3Data);
    };

    const { medianSalary, numJobsAvailable, currencyCode } = jobPostingData ?? {};

    if (showLoadingState) {
      return <CompactCareerRoleCardPlaceholder ref={ref} />;
    }

    if (!slug || !roleName) {
      return null;
    }

    const createHref = (roleSlug: string) => path.join(config.url.base, '/', 'career-academy', 'roles', roleSlug);

    const renderPreviewImage = () => {
      return roleCardImageUrl ? (
        <Imgix
          src={roleCardImageUrl}
          style={{ backgroundColor: imagePrimaryColor ?? 'transparent' }}
          maxWidth={IMAGE_DIMENSIONS.MAX_WIDTH}
          maxHeight={IMAGE_DIMENSIONS.MAX_HEIGHT}
          alt=""
        />
      ) : null;
    };

    const commonProductCardProps: React.ComponentProps<typeof ProductCard> = {
      ref: combinedRef,
      productType: 'role',
      renderPreviewImage,
      title: {
        name: roleName,
        linkProps: {
          href: createHref(slug),
          tabIndex: -1, // title should not be focusable
        },
      },
      onKeyPress: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          handleSelectCard();
        }
      },
      onClick: handleSelectCard,
      variant: 'grid',
      tabIndex: 0,
      css: [styles.productCard, styles.imagePrimaryColor(imagePrimaryColor)],
    };

    const renderMobileBody = () => (
      <div css={styles.bodyContainer}>
        <div css={styles.roleStatsContainer}>
          {medianSalary && currencyCode && <SalaryInfo medianSalary={medianSalary} currencyCode={currencyCode} />}
        </div>
      </div>
    );

    const renderDesktopBody = () => (
      <div css={styles.bodyContainer}>
        <VisuallyHidden component="span">{_t('description:')}</VisuallyHidden>
        <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.description}>
          {description}
        </Typography2>

        <div css={styles.roleStatsContainer}>
          {medianSalary && currencyCode && <SalaryInfo medianSalary={medianSalary} currencyCode={currencyCode} />}
          {numJobsAvailable && <JobsAvailableInfo numJobsAvailable={numJobsAvailable} />}
        </div>
      </div>
    );

    return (
      <div css={styles.wrapper}>
        {/* Mobile variant */}
        <Hidden smUp css={styles.container}>
          <ProductCard {...commonProductCardProps} body={renderMobileBody()} />
        </Hidden>

        {/* Desktop variant */}
        <Hidden only="xs" css={styles.container}>
          <ProductCard {...commonProductCardProps} body={renderDesktopBody()} />
        </Hidden>
      </div>
    );
  }
);

export default CompactCareerRoleCard;
